/**
 * React Query Client Configuration
 * Central configuration for all query and mutation defaults
 */

import { QueryClient } from '@tanstack/react-query'
import type { SlackAlertType } from '@/types/slackAlerts.types'
import type { RollupExperienceType } from '@/components/shared/ExperienceRollupCard'
import type {
  AnalyticsFilters,
  AnalyticsRecordsMetric,
  AnnualReviewMetric,
  CleaningFilters,
  ForecastQueueFilters,
  SpecialistTimeGrouping,
} from '@/types/backofficeAnalytics.types'

/**
 * Default options for all queries
 */
const defaultQueryOptions = {
  queries: {
    // Data is considered fresh for 5 minutes
    staleTime: 1000 * 60 * 5,
    // Cache data for 10 minutes
    gcTime: 1000 * 60 * 10,
    // Retry failed requests once
    retry: 1,
    // Refetch on window focus, but respect staleTime - queries still within
    // their 5-min staleTime window are not refetched on every focus. Using
    // 'always' here caused a refetch storm (incl. the heavy full-book query)
    // on every window focus.
    refetchOnWindowFocus: true,
    // Refetch on reconnect
    refetchOnReconnect: true,
  },
  mutations: {
    // Mutations are not retried by default - retrying a DELETE or a
    // download-by-id on a 404 is never correct and doubles server error logs.
    // Opt-in per-mutation when a transient retry is actually desirable.
    retry: 0,
  },
}

/**
 * Create and configure the QueryClient instance
 */
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
})

/**
 * Query keys for consistent cache management
 * Using this pattern makes it easier to invalidate related queries
 */
export const queryKeys = {
  clients: {
    all: ['clients'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.clients.all, 'list', filters] as const,
    favorites: (userId: string) =>
      [...queryKeys.clients.all, 'favorites', userId] as const,
    recent: () => [...queryKeys.clients.all, 'recent'] as const,
    detail: (id: string) => [...queryKeys.clients.all, 'detail', id] as const,
    accountRollups: (id: string) =>
      [...queryKeys.clients.all, 'accountRollups', id] as const,
    netWorthHistory: (id: string) =>
      [...queryKeys.clients.all, 'netWorthHistory', id] as const,
    meetingTypes: () => [...queryKeys.clients.all, 'meetingTypes'] as const,
    accountsMetrics: (filters?: unknown) =>
      [...queryKeys.clients.all, 'accountsMetrics', filters] as const,
    /** sc-811 Part 2 - a client's Annual Review history (§3/§6). */
    annualReviews: (clientId: string) =>
      [...queryKeys.clients.all, 'annualReviews', clientId] as const,
    annualReviewNotificationMute: (clientId: string) =>
      [
        ...queryKeys.clients.all,
        'annualReviewNotificationMute',
        clientId,
      ] as const,
    /**
     * sc-1347 - resolves a client from a HubSpot contact id, backing the
     * `/annual-review/start?hubspotContactId=` external entry point.
     */
    byHubspotContact: (hubspotContactId: string) =>
      [...queryKeys.clients.all, 'byHubspotContact', hubspotContactId] as const,
    /** sc-811 Part 2 §2 REVISED - the escape-plan-date confirmation queue. */
    pendingEscapePlanConfirmations: () =>
      [...queryKeys.clients.all, 'pendingEscapePlanConfirmations'] as const,
    /**
     * sc-1340 - a client's stored payment methods (advisor-scoped, keyed on
     * clientId). Distinct from `paymentMethods` below, the backoffice's
     * billing-profile-keyed equivalent.
     */
    paymentMethods: (clientId: string) =>
      [...queryKeys.clients.all, 'paymentMethods', clientId] as const,
  },
  forecasts: {
    all: ['forecasts'] as const,
    drafts: () => [...queryKeys.forecasts.all, 'drafts'] as const,
    published: () => [...queryKeys.forecasts.all, 'published'] as const,
    detail: (id: string) => [...queryKeys.forecasts.all, 'detail', id] as const,
    byClient: (clientId: string) =>
      [...queryKeys.forecasts.all, 'byClient', clientId] as const,
    adminFiles: (id: string) =>
      [...queryKeys.forecasts.all, 'adminFiles', id] as const,
    adminFileHistory: (id: string) =>
      [...queryKeys.forecasts.all, 'adminFileHistory', id] as const,
    adminAuditLog: (id: string) =>
      [...queryKeys.forecasts.all, 'adminAuditLog', id] as const,
    escapePlanReport: (forecastId: string) =>
      [...queryKeys.forecasts.all, 'escapePlanReport', forecastId] as const,
    rothologyReport: (forecastId: string) =>
      [...queryKeys.forecasts.all, 'rothologyReport', forecastId] as const,
    reportJobStatus: (forecastId: string, jobId: string) =>
      [
        ...queryKeys.forecasts.all,
        'reportJobStatus',
        forecastId,
        jobId,
      ] as const,
    escapePlanReportJobStatus: (forecastId: string, jobId: string) =>
      [
        ...queryKeys.forecasts.all,
        'escapePlanReportJobStatus',
        forecastId,
        jobId,
      ] as const,
  },
  forecastLabels: {
    all: ['forecastLabels'] as const,
    list: () => [...queryKeys.forecastLabels.all, 'list'] as const,
  },
  /** Round 13 — Backoffice "Forecast group names" admin list (distinct from `forecastGroups.nameSuggestions`, the per-client picker query it manages). */
  forecastGroupNames: {
    all: ['forecastGroupNames'] as const,
    list: () => [...queryKeys.forecastGroupNames.all, 'list'] as const,
  },
  terms: {
    all: ['terms'] as const,
    /** Every terms type + its full version history (sc-1388 backoffice manager). */
    groups: () => [...queryKeys.terms.all, 'groups'] as const,
    detail: (id: string) => [...queryKeys.terms.all, 'detail', id] as const,
    /** A client's terms-acceptance history. */
    clientAcceptances: (clientId: string) =>
      [...queryKeys.terms.all, 'client-acceptances', clientId] as const,
  },
  clientFiles: {
    all: ['clientFiles'] as const,
    list: (clientId: string) =>
      [...queryKeys.clientFiles.all, 'list', clientId] as const,
    preview: (clientId: string, fileId: string) =>
      [...queryKeys.clientFiles.all, 'preview', clientId, fileId] as const,
    activity: (clientId: string) =>
      [...queryKeys.clientFiles.all, 'activity', clientId] as const,
    /** One page of a client's Trash (sc-1352). */
    trash: (clientId: string, page: number) =>
      [...queryKeys.clientFiles.all, 'trash', clientId, page] as const,
    /** Prefix covering every Trash page of a client, for invalidation. */
    trashAll: (clientId: string) =>
      [...queryKeys.clientFiles.all, 'trash', clientId] as const,
  },
  users: {
    all: ['users'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.users.all, 'list', filters] as const,
    /** The signed-in user's identity, department and permissions (sc-1215). */
    me: () => [...queryKeys.users.all, 'me'] as const,
  },
  permissions: {
    all: ['permissions'] as const,
    /** The database-owned catalog of every permission that exists. */
    catalog: () => [...queryKeys.permissions.all, 'catalog'] as const,
    byUser: (userId: string) =>
      [...queryKeys.permissions.all, 'byUser', userId] as const,
  },
  classification: {
    all: ['classification'] as const,
    individualAccounts: (forecastId: string) =>
      [...queryKeys.classification.all, 'accounts', forecastId] as const,
    accountTypes: (forecastId: string) =>
      [...queryKeys.classification.all, 'types', forecastId] as const,
    deletedAccounts: (forecastId: string) =>
      [...queryKeys.classification.all, 'deleted', forecastId] as const,
    /** sc-1413: per-strategy Annuity Withdrawals file gate. */
    annuityWithdrawalsStatus: (forecastId: string) =>
      [
        ...queryKeys.classification.all,
        'annuityWithdrawalsStatus',
        forecastId,
      ] as const,
  },
  escapePlan: {
    all: ['escapePlan'] as const,
    active: (clientId: string) =>
      [...queryKeys.escapePlan.all, 'active', clientId] as const,
    /** sc-608 PR 2 — a client's current DRAFT/SHARED comparative recommendation sets, plus history. */
    recommendationSets: (clientId: string) =>
      [...queryKeys.escapePlan.all, 'recommendationSets', clientId] as const,
    /** sc-608 PR 2 — the wizard's step 1 candidate (forecast, strategy) list, resolved against the current draft. */
    recommendationCandidates: (clientId: string) =>
      [
        ...queryKeys.escapePlan.all,
        'recommendationCandidates',
        clientId,
      ] as const,
    /** Prefix of every one of a client's {@link recommendationComparability} queries, whatever the set — for invalidating after a forecast-group move. */
    recommendationComparabilityByClient: (clientId: string) =>
      [
        ...queryKeys.escapePlan.all,
        'recommendationComparability',
        clientId,
      ] as const,
    /** sc-608 PR 2 — the live comparability poll for one recommendation set. */
    recommendationComparability: (clientId: string, setId: string) =>
      [
        ...queryKeys.escapePlan.recommendationComparabilityByClient(clientId),
        setId,
      ] as const,
    /** Prefix of every one of a client's {@link recommendationComparison} queries, whatever the set — for invalidating after a forecast-group move. */
    recommendationComparisonByClient: (clientId: string) =>
      [
        ...queryKeys.escapePlan.all,
        'recommendationComparison',
        clientId,
      ] as const,
    /** sc-608 PR 2 — one set's client-style comparison table (wizard steps 2–3, Present dialog). */
    recommendationComparison: (clientId: string, setId: string) =>
      [
        ...queryKeys.escapePlan.recommendationComparisonByClient(clientId),
        setId,
      ] as const,
    /** sc-608 PR 2 decision B — one forecast's tax-adjusted net worth per candidate strategy, fetched on demand when that forecast's group is expanded in the wizard's step 1. Keyed per forecast (not per client) so expanding one group never invalidates another's already-loaded figures. */
    recommendationCandidateNetWorth: (clientId: string, forecastId: string) =>
      [
        ...queryKeys.escapePlan.all,
        'recommendationCandidateNetWorth',
        clientId,
        forecastId,
      ] as const,
    /** sc-608 summary email — the prefilled draft (defaults, recipients, attachment choices) for one recommendation set. */
    summaryEmailDraft: (clientId: string, setId: string) =>
      [
        ...queryKeys.escapePlan.all,
        'summaryEmailDraft',
        clientId,
        setId,
      ] as const,
    /** sc-608 summary email — the server-rendered preview of one set's email for one exact input. */
    summaryEmailPreview: (clientId: string, setId: string, input: unknown) =>
      [
        ...queryKeys.escapePlan.all,
        'summaryEmailPreview',
        clientId,
        setId,
        input,
      ] as const,
    /** sc-608 summary email — the emails already sent (or queued) for one recommendation set, newest first. */
    summaryEmailHistory: (clientId: string, setId: string) =>
      [
        ...queryKeys.escapePlan.all,
        'summaryEmailHistory',
        clientId,
        setId,
      ] as const,
    /** Prefix of every one of a client's {@link summaryEmailHistory} queries — for invalidating after a send. */
    summaryEmailHistoryByClient: (clientId: string) =>
      [...queryKeys.escapePlan.all, 'summaryEmailHistory', clientId] as const,
  },
  /** sc-608 PR 2 continuation — a client's stored FORECAST GROUPS (`ForecastsTable`'s nested containers, the recommendations wizard's step 1 grouping). */
  forecastGroups: {
    all: ['forecastGroups'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.forecastGroups.all, 'byClient', clientId] as const,
    /** Round 13 — `GroupNamePopover`'s "Suggested" list. */
    nameSuggestions: (clientId: string) =>
      [...queryKeys.forecastGroups.all, 'nameSuggestions', clientId] as const,
  },
  dataGathering: {
    all: ['dataGathering'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.dataGathering.all, 'byClient', clientId] as const,
    detail: (formId: string) =>
      [...queryKeys.dataGathering.all, 'detail', formId] as const,
    history: (clientId: string) =>
      [...queryKeys.dataGathering.all, 'history', clientId] as const,
    ruleChecks: (formId: string) =>
      [...queryKeys.dataGathering.all, 'ruleChecks', formId] as const,
    attachments: (formId: string) =>
      [...queryKeys.dataGathering.all, 'attachments', formId] as const,
    changes: (formId: string) =>
      [...queryKeys.dataGathering.all, 'changes', formId] as const,
    portalStatus: (clientId: string) =>
      [...queryKeys.dataGathering.all, 'portalStatus', clientId] as const,
    activity: (formId: string) =>
      [...queryKeys.dataGathering.all, 'activity', formId] as const,
    activityDiff: (formId: string, activityId: string) =>
      [
        ...queryKeys.dataGathering.all,
        'activity',
        formId,
        'diff',
        activityId,
      ] as const,
    fieldHistory: (formId: string) =>
      [...queryKeys.dataGathering.all, 'fieldHistory', formId] as const,
    approvalWindow: (formId: string) =>
      [...queryKeys.dataGathering.all, 'approvalWindow', formId] as const,
  },
  rmd: {
    all: ['rmd'] as const,
    detail: (id: string) => [...queryKeys.rmd.all, 'detail', id] as const,
    withPdf: (id: string) => [...queryKeys.rmd.all, 'withPdf', id] as const,
    pdfStatus: (rmdId: string, jobId: string) =>
      [...queryKeys.rmd.all, 'pdfStatus', rmdId, jobId] as const,
    hubspotStatus: (id: string) =>
      [...queryKeys.rmd.all, 'hubspotStatus', id] as const,
    sessions: (
      page?: number,
      perPage?: number,
      search?: string,
      clientId?: string,
    ) =>
      page !== undefined && perPage !== undefined
        ? ([
            ...queryKeys.rmd.all,
            'sessions',
            page,
            perPage,
            search ?? null,
            clientId ?? null,
          ] as const)
        : ([...queryKeys.rmd.all, 'sessions'] as const),
  },
  forecastFiles: {
    all: ['forecastFiles'] as const,
    byForecast: (forecastId: string) =>
      [...queryKeys.forecastFiles.all, 'byForecast', forecastId] as const,
  },
  irsTables: {
    all: ['irsTables'] as const,
    availableYears: () =>
      [...queryKeys.irsTables.all, 'availableYears'] as const,
    data: (year: number) => [...queryKeys.irsTables.all, 'data', year] as const,
    jointLifeLookup: (year: number, ownerAge: string, spouseAge: string) =>
      [
        ...queryKeys.irsTables.all,
        'jointLifeLookup',
        year,
        ownerAge,
        spouseAge,
      ] as const,
  },
  hubspot: {
    all: ['hubspot'] as const,
    // sc-1411 — the client page's service ticket cards, and one ticket's
    // merged activity feed (fetched lazily, only when the tray's tab opens).
    clientJourneyTickets: (clientId: string) =>
      [...queryKeys.hubspot.all, 'client-journey-tickets', clientId] as const,
    journeyTicketActivity: (clientId: string, hubspotId: string) =>
      [
        ...queryKeys.hubspot.all,
        'journey-ticket-activity',
        clientId,
        hubspotId,
      ] as const,
    // sc-1411 follow-up — the blue IE Deal card beside the ticket card in
    // the same row. The query-key SEGMENT stays 'integration-experience-card'
    // (matches the API route, which is unchanged) even though the exported
    // key name and the on-screen title are now "IE Deal" — renaming the
    // wire-level string would just be churn.
    ieDealCard: (clientId: string) =>
      [
        ...queryKeys.hubspot.all,
        'integration-experience-card',
        clientId,
      ] as const,
    userSyncStatus: () =>
      [...queryKeys.hubspot.all, 'user-sync-status'] as const,
    clientSyncStatus: () =>
      [...queryKeys.hubspot.all, 'client-sync-status'] as const,
    productSyncStatus: () =>
      [...queryKeys.hubspot.all, 'product-sync-status'] as const,
    meetingsSyncStatus: () =>
      [...queryKeys.hubspot.all, 'meetings-sync-status'] as const,
    meetingTypeMergePreview: (sourceType: string) =>
      [
        ...queryKeys.hubspot.all,
        'meeting-type-merge-preview',
        sourceType,
      ] as const,
    invoiceSyncStatus: () =>
      [...queryKeys.hubspot.all, 'invoice-sync-status'] as const,
    dealsSyncStatus: () =>
      [...queryKeys.hubspot.all, 'deals-sync-status'] as const,
    ticketsSyncStatus: () =>
      [...queryKeys.hubspot.all, 'tickets-sync-status'] as const,
    quotesSyncStatus: () =>
      [...queryKeys.hubspot.all, 'quotes-sync-status'] as const,
    emailAttachmentSyncStatus: () =>
      [...queryKeys.hubspot.all, 'email-attachment-sync-status'] as const,
    webhookSubscriptions: () =>
      [...queryKeys.hubspot.all, 'webhook-subscriptions'] as const,
    webhookEvents: (filters?: unknown) =>
      [...queryKeys.hubspot.all, 'webhook-events', filters] as const,
    webhookEvent: (id: string) =>
      [...queryKeys.hubspot.all, 'webhook-event', id] as const,
    syncBatches: (filters?: unknown) =>
      [...queryKeys.hubspot.all, 'sync-batches', filters] as const,
    syncBatch: (id: string) =>
      [...queryKeys.hubspot.all, 'sync-batch', id] as const,
    records: (filters?: unknown) =>
      [...queryKeys.hubspot.all, 'records', filters] as const,
    invoiceOwnerFixPreview: () =>
      [...queryKeys.hubspot.all, 'invoice-owner-fix-preview'] as const,
    invoiceDealFixPreview: () =>
      [...queryKeys.hubspot.all, 'invoice-deal-fix-preview'] as const,
    invoiceDealOptions: (invoiceId: string) =>
      [...queryKeys.hubspot.all, 'invoice-deal-options', invoiceId] as const,
    dealStageOptions: () =>
      [...queryKeys.hubspot.all, 'deal-stage-options'] as const,
    invoiceExperienceFixPreview: () =>
      [...queryKeys.hubspot.all, 'invoice-experience-fix-preview'] as const,
    lookupObject: (params: unknown) =>
      [...queryKeys.hubspot.all, 'lookup-object', params] as const,
    lookupPropertyValue: (params: unknown) =>
      [...queryKeys.hubspot.all, 'lookup-property-value', params] as const,
    clientAssociations: (clientId: string) =>
      [...queryKeys.hubspot.all, 'client-associations', clientId] as const,
    meetingRecordings: (clientId: string, includeWithoutRecording?: boolean) =>
      [
        ...queryKeys.hubspot.all,
        'meeting-recordings',
        clientId,
        Boolean(includeWithoutRecording),
      ] as const,
    clientJourney: (clientId: string) =>
      [...queryKeys.hubspot.all, 'client-journey', clientId] as const,
    // sc-1531: nested under `clientJourney` so a journey refetch drops it too.
    escapePlanDeliveryOptions: (clientId: string) =>
      [
        ...queryKeys.hubspot.clientJourney(clientId),
        'escape-plan-delivery-options',
      ] as const,
    // sc-1396: nested under `clientJourney` on purpose, so invalidating the
    // journey (every meetings sync does) refreshes it as well.
    clientJourneyUnsettledOperations: (clientId: string) =>
      [
        ...queryKeys.hubspot.clientJourney(clientId),
        'unsettled-operations',
      ] as const,
    // sc-1396 (plan v3): the embedded booking's two reads, nested under
    // `clientJourney` for the same reason.
    clientJourneySessionsStatus: (clientId: string) =>
      [
        ...queryKeys.hubspot.clientJourney(clientId),
        'sessions-status',
      ] as const,
    clientJourneyBookingEmbed: (clientId: string) =>
      [...queryKeys.hubspot.clientJourney(clientId), 'booking-embed'] as const,
    clientJourneyDataGatheringLinks: (clientId: string) =>
      [
        ...queryKeys.hubspot.clientJourney(clientId),
        'data-gathering-links',
      ] as const,
    clientObjectDetail: (
      clientId: string,
      objectType: string,
      hubspotId: string,
    ) =>
      [
        ...queryKeys.hubspot.all,
        'client-object-detail',
        clientId,
        objectType,
        hubspotId,
      ] as const,
  },
  stripe: {
    all: ['stripe'] as const,
    customers: (filters?: unknown) =>
      [...queryKeys.stripe.all, 'customers', filters] as const,
    payments: (filters?: unknown) =>
      [...queryKeys.stripe.all, 'payments', filters] as const,
    invoices: (filters?: unknown) =>
      [...queryKeys.stripe.all, 'invoices', filters] as const,
    subscriptions: (filters?: unknown) =>
      [...queryKeys.stripe.all, 'subscriptions', filters] as const,
    syncStatus: (object: string) =>
      [...queryKeys.stripe.all, 'sync-status', object] as const,
    syncBatches: (filters?: unknown) =>
      [...queryKeys.stripe.all, 'sync-batches', filters] as const,
    syncBatch: (id: string) =>
      [...queryKeys.stripe.all, 'sync-batch', id] as const,
    webhookEvents: (filters?: unknown) =>
      [...queryKeys.stripe.all, 'webhook-events', filters] as const,
    webhookEvent: (id: string) =>
      [...queryKeys.stripe.all, 'webhook-event', id] as const,
    automations: () => [...queryKeys.stripe.all, 'automations'] as const,
    automationLogs: (filters?: unknown) =>
      [...queryKeys.stripe.all, 'automation-logs', filters] as const,
    automationLogEventTypes: () =>
      [...queryKeys.stripe.all, 'automation-log-event-types'] as const,
    databaseAutomations: () =>
      [...queryKeys.stripe.all, 'database-automations'] as const,
    drift: () => [...queryKeys.stripe.all, 'drift'] as const,
    driftStatus: () => [...queryKeys.stripe.all, 'drift-status'] as const,
  },
  sessions: {
    all: ['sessions'] as const,
    list: (userId: string) =>
      [...queryKeys.sessions.all, 'list', userId] as const,
  },
  rightCapitalAdvisors: {
    all: ['rightCapitalAdvisors'] as const,
  },
  clientRoleSync: {
    all: ['clientRoleSync'] as const,
    status: (clientId: string) =>
      [...queryKeys.clientRoleSync.all, 'status', clientId] as const,
  },
  checkout: {
    all: ['checkout'] as const,
    catalog: () => [...queryKeys.checkout.all, 'catalog'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.checkout.all, 'client', clientId] as const,
    hubspotDeals: (clientId: string) =>
      [...queryKeys.checkout.all, 'client', clientId, 'hubspot-deals'] as const,
    hubspotQuotes: (clientId: string, dealId: string) =>
      [
        ...queryKeys.checkout.all,
        'client',
        clientId,
        'hubspot-deals',
        dealId,
        'quotes',
      ] as const,
    backofficeList: (filters?: unknown) =>
      [...queryKeys.checkout.all, 'backoffice-list', filters] as const,
    failureReport: (days: number) =>
      [...queryKeys.checkout.all, 'failure-report', days] as const,
  },
  billingSavedViews: {
    all: ['billingSavedViews'] as const,
  },
  analyticsSavedViews: {
    all: ['analyticsSavedViews'] as const,
  },
  billingStripeLookup: {
    all: ['billingStripeLookup'] as const,
    byId: (stripeId: string) =>
      [...queryKeys.billingStripeLookup.all, stripeId] as const,
  },
  billingQueue: {
    all: ['billingQueue'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.billingQueue.all, 'list', filters] as const,
    batch: (batchId: string) =>
      [...queryKeys.billingQueue.all, 'batch', batchId] as const,
    planOptions: () => [...queryKeys.billingQueue.all, 'plan-options'] as const,
    billingPreview: (formId: string) =>
      [...queryKeys.billingQueue.all, 'billing-preview', formId] as const,
  },
  /** Billing audit drawer's activity sections — own namespace since the drawer opens from BOTH Checkouts and Charging Queue rows. */
  billingActivity: {
    all: ['billingActivity'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.billingActivity.all, 'client', clientId] as const,
  },
  paidCustomers: {
    all: ['paidCustomers'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.paidCustomers.all, 'list', filters] as const,
    detail: (rowKey: string) =>
      [...queryKeys.paidCustomers.all, 'detail', rowKey] as const,
    subscriptionManagement: (stripeSubscriptionId: string) =>
      [
        ...queryKeys.paidCustomers.all,
        'subscription-management',
        stripeSubscriptionId,
      ] as const,
  },
  paymentMethods: {
    all: ['paymentMethods'] as const,
    list: (clientBillingProfileId: string) =>
      [
        ...queryKeys.paymentMethods.all,
        'list',
        clientBillingProfileId,
      ] as const,
  },
  plannerBook: {
    all: ['plannerBook'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.plannerBook.all, 'list', filters] as const,
    accounts: (plannerId: string) =>
      [...queryKeys.plannerBook.all, 'accounts', plannerId] as const,
  },
  churned: {
    all: ['churned'] as const,
    list: () => [...queryKeys.churned.all, 'list'] as const,
  },
  renewals: {
    all: ['renewals'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.renewals.all, 'list', filters] as const,
    plans: () => [...queryKeys.renewals.all, 'plans'] as const,
  },
  /** Charging Queue → Recurring: upcoming charges on live subscriptions. */
  recurringCharges: {
    all: ['recurringCharges'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.recurringCharges.all, 'list', filters] as const,
  },
  /** TEMPORARY - sc-624 parity admin tool. Delete after typed-tables cutover. */
  parity: {
    all: ['parity'] as const,
    list: (filters?: unknown) =>
      [...queryKeys.parity.all, 'list', filters] as const,
    detail: (forecastId: string) =>
      [...queryKeys.parity.all, 'detail', forecastId] as const,
    bucketPaths: (forecastId: string) =>
      [...queryKeys.parity.all, 'bucketPaths', forecastId] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (userId: string, filters?: unknown) =>
      [...queryKeys.notifications.all, 'list', userId, filters] as const,
    unreadCount: (userId: string) =>
      [...queryKeys.notifications.all, 'unreadCount', userId] as const,
  },
  rothRadarAlerts: {
    all: ['rothRadarAlerts'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.rothRadarAlerts.all, 'byClient', clientId] as const,
  },
  /**
   * sc-1266: the CPA share links behind the Integration Experience card's
   * history sheet. The referral rows themselves (`CpaReferral`) are NOT
   * keyed here: they ride on `queryKeys.clients.detail`, same as
   * `isRothRadarEntitled`, so the three referral mutations invalidate that
   * key instead. The referral timeline (`CpaReferralEvent`, sc-1266
   * follow-up) IS keyed here as `cpaReferralEvents` below, since it has no
   * home on the client-detail payload; the three mutations invalidate that
   * key too, alongside `queryKeys.clients.detail`.
   */
  cpaTaxShareLinks: {
    all: ['cpaTaxShareLinks'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.cpaTaxShareLinks.all, 'byClient', clientId] as const,
  },
  cpaReferralEvents: {
    all: ['cpaReferralEvents'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.cpaReferralEvents.all, 'byClient', clientId] as const,
  },
  rothRadarBook: {
    all: ['rothRadarBook'] as const,
    // clientIds is sorted by the caller so re-renders with the same set of
    // ids (in a different order) hit the same cache entry instead of
    // thrashing.
    summary: (clientIds: ReadonlyArray<string>) =>
      [...queryKeys.rothRadarBook.all, 'summary', clientIds] as const,
    // sc-1329 — keyed by the filters, not by a client list, because both of
    // these are computed server-side over the whole filtered population. Under
    // `all` so the six radar mutations that already invalidate by that prefix
    // refresh them too.
    metrics: (filters?: unknown) =>
      [...queryKeys.rothRadarBook.all, 'metrics', filters] as const,
    selectableClients: (filters?: unknown) =>
      [...queryKeys.rothRadarBook.all, 'selectableClients', filters] as const,
  },
  rothRadarPositions: {
    all: ['rothRadarPositions'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.rothRadarPositions.all, 'byClient', clientId] as const,
    // sc-1771: the audit trail of hand-scoping changes.
    scopingAdjustments: (clientId: string) =>
      [
        ...queryKeys.rothRadarPositions.all,
        'scopingAdjustments',
        clientId,
      ] as const,
  },
  // sc-1205: pro-rata conversion calculator.
  rothRadarProRata: {
    all: ['rothRadarProRata'] as const,
    // Prefix (no `basis`) so a universe mutation can invalidate every
    // preview cached for this client regardless of which path/override it
    // was previewed with - see useClientRothRadarUniverse.ts.
    previewAll: (clientId: string) =>
      [
        ...queryKeys.rothRadarProRata.all,
        'preview',
        'byClient',
        clientId,
      ] as const,
    // `basis` is `unknown` here (not `RothRadarProRataRunBasis`), matching
    // this file's existing convention for opaque cache-key params (see
    // `notifications.list`'s `filters?: unknown`) - the key only needs
    // `basis` to be structurally comparable, not typed.
    preview: (clientId: string, basis: unknown) =>
      [...queryKeys.rothRadarProRata.previewAll(clientId), basis] as const,
    runsAll: (clientId: string) =>
      [
        ...queryKeys.rothRadarProRata.all,
        'runs',
        'byClient',
        clientId,
      ] as const,
    runs: (clientId: string, params?: { skip?: number; take?: number }) =>
      [...queryKeys.rothRadarProRata.runsAll(clientId), params] as const,
    // sc-1206: the copy-ready kickoff summary for one saved run.
    runSummary: (clientId: string, runId: string | null) =>
      [
        ...queryKeys.rothRadarProRata.all,
        'runSummary',
        clientId,
        runId,
      ] as const,
  },
  rothRadarGroups: {
    all: ['rothRadarGroups'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.rothRadarGroups.all, 'byClient', clientId] as const,
  },
  rothRadarSettings: {
    all: ['rothRadarSettings'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.rothRadarSettings.all, 'byClient', clientId] as const,
  },
  rothRadarTransactions: {
    all: ['rothRadarTransactions'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.rothRadarTransactions.all, 'byClient', clientId] as const,
  },
  rothRadarNotifications: {
    all: ['rothRadarNotifications'] as const,
    byClient: (clientId: string) =>
      [...queryKeys.rothRadarNotifications.all, 'byClient', clientId] as const,
    /** sc-1386. The resend dialog's rebuilt-email preview — a dry-run render,
     * scoped by both ids since a client has many notifications. */
    resendPreview: (clientId: string, notificationId: string) =>
      [
        ...queryKeys.rothRadarNotifications.byClient(clientId),
        'resendPreview',
        notificationId,
      ] as const,
  },
  rothRadarBulkNotify: {
    all: ['rothRadarBulkNotify'] as const,
    run: (runId: string) =>
      [...queryKeys.rothRadarBulkNotify.all, 'run', runId] as const,
  },
  clientHelpers: {
    all: ['clientHelpers'] as const,
    list: (clientId: string) =>
      [...queryKeys.clientHelpers.all, 'list', clientId] as const,
    portalStatus: (clientId: string, helperId: string) =>
      [
        ...queryKeys.clientHelpers.all,
        'portalStatus',
        clientId,
        helperId,
      ] as const,
  },
  libraryFiles: {
    all: ['libraryFiles'] as const,
    list: () => [...queryKeys.libraryFiles.all, 'list'] as const,
    visibility: (fileId: string) =>
      [...queryKeys.libraryFiles.all, 'visibility', fileId] as const,
    folderVisibility: (path: string) =>
      [...queryKeys.libraryFiles.all, 'folderVisibility', path] as const,
  },
  sharefileMigration: {
    all: ['sharefileMigration'] as const,
    tree: () => [...queryKeys.sharefileMigration.all, 'tree'] as const,
    links: () => [...queryKeys.sharefileMigration.all, 'links'] as const,
    folderChildren: (folderId: string) =>
      [
        ...queryKeys.sharefileMigration.all,
        'folderChildren',
        folderId,
      ] as const,
    clientAccess: (clientId: string) =>
      [...queryKeys.sharefileMigration.all, 'clientAccess', clientId] as const,
    clientCandidates: (clientId: string, q?: string) =>
      [
        ...queryKeys.sharefileMigration.all,
        'clientCandidates',
        clientId,
        q ?? '',
      ] as const,
    clientLinks: (clientId: string) =>
      [...queryKeys.sharefileMigration.all, 'clientLinks', clientId] as const,
    folderPreview: (clientId: string, driveFolderId: string) =>
      [
        ...queryKeys.sharefileMigration.all,
        'folderPreview',
        clientId,
        driveFolderId,
      ] as const,
  },
  backoffice: {
    all: ['backoffice'] as const,
    summary: () => [...queryKeys.backoffice.all, 'summary'] as const,
    // Nested under `backoffice` rather than a sibling root so invalidating
    // `queryKeys.backoffice.all` also clears analytics — as a separate root it
    // silently survived every backoffice invalidation.
    analytics: {
      all: ['backoffice', 'analytics'] as const,
      overview: (filters: AnalyticsFilters) =>
        [...queryKeys.backoffice.analytics.all, 'overview', filters] as const,
      timeseries: (filters: AnalyticsFilters, options?: unknown) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'timeseries',
          filters,
          options,
        ] as const,
      people: (filters: AnalyticsFilters) =>
        [...queryKeys.backoffice.analytics.all, 'people', filters] as const,
      specialistPeople: (
        filters: AnalyticsFilters,
        options?: { compare?: boolean },
      ) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'specialist-people',
          filters,
          options,
        ] as const,
      productivity: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'productivity',
          filters,
        ] as const,
      newPlansWorked: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'new-plans-worked',
          filters,
        ] as const,
      casesOpen: (filters: AnalyticsFilters) =>
        [...queryKeys.backoffice.analytics.all, 'cases-open', filters] as const,
      alternativeScenariosCompleted: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'alternative-scenarios-completed',
          filters,
        ] as const,
      errorsAddressed: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'errors-addressed',
          filters,
        ] as const,
      informationResolved: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'information-resolved',
          filters,
        ] as const,
      alternativeScenariosRequested: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'alternative-scenarios-requested',
          filters,
        ] as const,
      casesPulledBackToDataEntry: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'cases-pulled-back-to-data-entry',
          filters,
        ] as const,
      person: (userId: string, filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'person',
          userId,
          filters,
        ] as const,
      records: (
        metric: AnalyticsRecordsMetric,
        filters: AnalyticsFilters,
        paging: { page: number; pageSize: number; stage?: string },
      ) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'records',
          metric,
          filters,
          paging,
        ] as const,
      // No filters — the orphaned backlog is a standing snapshot with no
      // window to key on (see `OrphanedTickets`'s doc comment).
      orphanedTickets: () =>
        [...queryKeys.backoffice.analytics.all, 'orphaned-tickets'] as const,
      // No filters — like `orphanedTickets` above, `GET
      // /analytics/annual-reviews` takes no query params at all (not even
      // experience/person like the Forecasts queue), so there is nothing to
      // key on beyond the metric itself.
      annualReviews: () =>
        [...queryKeys.backoffice.analytics.all, 'annual-reviews'] as const,
      // The rows behind one Annual Reviews rollup tile (sc-1212 UX pass) —
      // `groupKey`/paging are part of the key, same "changing page is a
      // fresh fetch" reasoning as `records` above.
      annualReviewRecords: (
        metric: AnnualReviewMetric,
        paging: { page: number; pageSize: number; groupKey?: string },
      ) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'annual-reviews',
          'records',
          metric,
          paging,
        ] as const,
      planWork: (groupBy: string, filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'plan-work',
          groupBy,
          filters,
        ] as const,
      // Rework-vs-first-pass rollup beside Plan Work (sc-1212 round 3) — same
      // `filters` shape and same key nesting as `planWork` above, no `groupBy`
      // since this endpoint returns one rollup, not a grouped series.
      changeRequests: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'change-requests',
          filters,
        ] as const,
      epOutcomes: (filters: AnalyticsFilters) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'ep-outcomes',
          filters,
        ] as const,
      meetings: (filters: AnalyticsFilters) =>
        [...queryKeys.backoffice.analytics.all, 'meetings', filters] as const,
      meetingRecords: (
        filters: AnalyticsFilters,
        stage: string,
        paging: { page: number; pageSize: number },
      ) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'meetings',
          'records',
          stage,
          filters,
          paging,
        ] as const,
      // Only `experience`/`paging`/`horizonDays` — the endpoint ignores the
      // rest of `AnalyticsFilters` by design (see `AnalyticsUpcomingMeetings`'s
      // doc comment), so keying on the whole filter object would refetch on
      // a Range change that can't actually change the answer.
      upcomingMeetings: (
        experience: Array<RollupExperienceType>,
        paging: { page: number; pageSize: number },
        horizonDays: number | undefined,
      ) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'meetings',
          'upcoming',
          experience,
          paging,
          horizonDays ?? 'all',
        ] as const,
      specialistTime: (
        groupBy: SpecialistTimeGrouping,
        filters: AnalyticsFilters,
      ) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'specialist-time',
          groupBy,
          filters,
        ] as const,
      // Only `experience`/`person` — the endpoint ignores the rest of
      // `AnalyticsFilters` by design (see `AnalyticsForecastQueue`'s doc
      // comment), so keying on the whole filter object would refetch on a
      // Range change that can't actually change the answer.
      forecastQueue: (filters: ForecastQueueFilters) =>
        [...queryKeys.backoffice.analytics.forecastQueueAll, filters] as const,
      /**
       * Every forecast-queue query, whatever its filters — the invalidation
       * target for a manager action on ONE case (sc-1212 round 9).
       *
       * Auditing or parking a case used to invalidate
       * `analytics.all`, which refetches all thirteen queries behind the
       * Analytics page. Writing one note sent the whole page back to
       * skeletons; before the October query work that was minutes of spinner.
       * A manager action changes the queue and nothing else.
       */
      // Spelled out rather than spread from `all`: a non-function property
      // that reads `queryKeys` during its own initialisation is a circular
      // reference, which the function-valued keys above avoid by deferring to
      // call time. Kept adjacent to `forecastQueue` so the two cannot drift.
      forecastQueueAll: ['backoffice', 'analytics', 'forecast-queue'] as const,
      /**
       * The Needs Cleaned tab (sc-1212 audit round) — its OWN endpoint with
       * its own server-side paging, never a slice of the forecast-queue
       * payload.
       *
       * `filters` AND `paging` are both in the key, same reasoning as
       * `records`/`annualReviewRecords` above: the server does the slicing,
       * so turning a page is a fresh fetch rather than a client reshape of
       * one payload. `keepPreviousData` on the hook is what keeps that from
       * flashing the table empty.
       */
      cleaning: (
        filters: CleaningFilters,
        paging: { page: number; pageSize: number },
      ) =>
        [
          ...queryKeys.backoffice.analytics.all,
          'cleaning',
          filters,
          paging,
        ] as const,
    },
  },
  learning: {
    all: ['learning'] as const,
    courses: () => [...queryKeys.learning.all, 'courses'] as const,
    mine: () => [...queryKeys.learning.all, 'mine'] as const,
    onboarding: () => [...queryKeys.learning.all, 'onboarding'] as const,
    requests: () => [...queryKeys.learning.all, 'requests'] as const,
    terms: (kind: string) =>
      [...queryKeys.learning.all, 'terms', kind] as const,
    browse: (params: {
      search?: string
      categoryId?: string
      technologyId?: string
      page?: number
    }) => [...queryKeys.learning.all, 'browse', params] as const,
    report: (scope: string) =>
      [...queryKeys.learning.all, 'report', scope] as const,
    assignments: (versionId: string) =>
      [...queryKeys.learning.all, 'assignments', versionId] as const,
    learnerCourse: (slug: string) =>
      [...queryKeys.learning.all, 'learner-course', slug] as const,
    watchStats: () => [...queryKeys.learning.all, 'watch-stats'] as const,
    muxUsage: () => [...queryKeys.learning.all, 'mux-usage'] as const,
    library: (params: { kind?: string; q?: string; tag?: string }) =>
      [...queryKeys.learning.all, 'library', params] as const,
    transcript: (assetId: string) =>
      [...queryKeys.learning.all, 'transcript', assetId] as const,
    lessonVids: () => [...queryKeys.learning.all, 'lesson-vids'] as const,
    videoStatus: (assetId: string) =>
      [...queryKeys.learning.all, 'video-status', assetId] as const,
    vidsNeedingAttention: () =>
      [...queryKeys.learning.all, 'vids-needing-attention'] as const,
    videoChapters: (assetId: string) =>
      [...queryKeys.learning.all, 'video-chapters', assetId] as const,
    courseDiscussion: (courseId: string) =>
      [...queryKeys.learning.all, 'course-discussion', courseId] as const,
    /** Keyed by lesson LINEAGE, so a thread survives a revision (sc-1292). */
    discussion: (courseId: string, lessonLineageId: string) =>
      [
        ...queryKeys.learning.all,
        'discussion',
        courseId,
        lessonLineageId,
      ] as const,
    version: (versionId: string) =>
      [...queryKeys.learning.all, 'version', versionId] as const,
    attachments: (versionId: string) =>
      [...queryKeys.learning.all, 'attachments', versionId] as const,
    /** Signed bucket URL for one asset. Cached under its expiry (sc-1272). */
    mediaUrl: (assetId: string) =>
      [...queryKeys.learning.all, 'media-url', assetId] as const,
    needsAttention: () =>
      [...queryKeys.learning.all, 'needs-attention'] as const,
    history: (versionId: string) =>
      [...queryKeys.learning.all, 'history', versionId] as const,
    comments: (versionId: string) =>
      [...queryKeys.learning.all, 'comments', versionId] as const,
    revisionRequests: (versionId: string) =>
      [...queryKeys.learning.all, 'revision-requests', versionId] as const,
    quizAttempts: (lessonId: string) =>
      [...queryKeys.learning.all, 'quiz-attempts', lessonId] as const,
    videoPlayback: (assetId: string) =>
      [...queryKeys.learning.all, 'video-playback', assetId] as const,
    audiencePreview: (versionId: string) =>
      [...queryKeys.learning.all, 'audience-preview', versionId] as const,
  },
  plaid: {
    all: ['plaid'] as const,
    stats: () => [...queryKeys.plaid.all, 'stats'] as const,
    items: () => [...queryKeys.plaid.all, 'items'] as const,
    settings: () => [...queryKeys.plaid.all, 'settings'] as const,
    recipients: () => [...queryKeys.plaid.all, 'recipients'] as const,
  },
  journeyScheduling: {
    all: ['journeyScheduling'] as const,
    meetingTypes: () =>
      [...queryKeys.journeyScheduling.all, 'meeting-types'] as const,
    gaps: () => [...queryKeys.journeyScheduling.all, 'gaps'] as const,
    holidays: () => [...queryKeys.journeyScheduling.all, 'holidays'] as const,
    deliveryTarget: () =>
      [...queryKeys.journeyScheduling.all, 'delivery-target'] as const,
    // sc-1396: the Chili Piper webhook subscriptions of this environment and
    // the log of received deliveries. `events` is nested under
    // `chiliPiperWebhooks` so one invalidation refreshes both after a register.
    chiliPiperWebhooks: () =>
      [...queryKeys.journeyScheduling.all, 'chili-piper-webhooks'] as const,
    chiliPiperWebhookEvents: (query: {
      status?: string
      page?: number
      pageSize?: number
    }) =>
      [
        ...queryKeys.journeyScheduling.chiliPiperWebhooks(),
        'events',
        query,
      ] as const,
  },
  slackStalledAlerts: {
    all: ['slackStalledAlerts'] as const,
    status: () => [...queryKeys.slackStalledAlerts.all, 'status'] as const,
    settings: () => [...queryKeys.slackStalledAlerts.all, 'settings'] as const,
    episodes: () => [...queryKeys.slackStalledAlerts.all, 'episodes'] as const,
    activity: () => [...queryKeys.slackStalledAlerts.all, 'activity'] as const,
  },
  slackAlerts: {
    all: ['slackAlerts'] as const,
    destinations: (alertType: SlackAlertType) =>
      [...queryKeys.slackAlerts.all, 'destinations', alertType] as const,
    slackChannels: () =>
      [...queryKeys.slackAlerts.all, 'slack-channels'] as const,
    rothRadarStatus: () =>
      [...queryKeys.slackAlerts.all, 'roth-radar-status'] as const,
  },
  // sc-863: backoffice meeting transcripts page (Zoom + Avoma). Every key sits
  // under `all`, so one invalidation refreshes the status, the lists and the
  // open detail together.
  meetingTranscripts: {
    all: ['meetingTranscripts'] as const,
    status: () => [...queryKeys.meetingTranscripts.all, 'status'] as const,
    insights: () => [...queryKeys.meetingTranscripts.all, 'insights'] as const,
    meetingsAll: () =>
      [...queryKeys.meetingTranscripts.all, 'meetings'] as const,
    meetings: (query: unknown) =>
      [...queryKeys.meetingTranscripts.meetingsAll(), query] as const,
    meetingAll: () => [...queryKeys.meetingTranscripts.all, 'meeting'] as const,
    meeting: (id: string) =>
      [...queryKeys.meetingTranscripts.meetingAll(), id] as const,
    meetingTypes: () =>
      [...queryKeys.meetingTranscripts.all, 'meetingTypes'] as const,
    // The call tags with their rules (and whether the viewer holds the
    // confidential permission).
    tags: () => [...queryKeys.meetingTranscripts.all, 'tags'] as const,
    // Meeting outcomes (Phase C). One prefix for the list, the summary and the
    // rows, so a decision refreshes all three; the list's pages are kept
    // apart by the query.
    outcomesAll: () =>
      [...queryKeys.meetingTranscripts.all, 'outcomes'] as const,
    outcomesList: (query: unknown) =>
      [...queryKeys.meetingTranscripts.outcomesAll(), 'list', query] as const,
    outcomesListAll: () =>
      [...queryKeys.meetingTranscripts.outcomesAll(), 'list'] as const,
    outcomesSummary: () =>
      [...queryKeys.meetingTranscripts.outcomesAll(), 'summary'] as const,
    outcome: (hubspotMeetingId: string) =>
      [
        ...queryKeys.meetingTranscripts.outcomesAll(),
        'row',
        hubspotMeetingId,
      ] as const,
    hubspotCandidatesAll: (id: string) =>
      [...queryKeys.meetingTranscripts.all, 'hubspotCandidates', id] as const,
    // One list per client asked for: `null` is the recording's own client.
    hubspotCandidates: (id: string, clientId?: string | null) =>
      [
        ...queryKeys.meetingTranscripts.hubspotCandidatesAll(id),
        clientId ?? null,
      ] as const,
    transcript: (id: string) =>
      [...queryKeys.meetingTranscripts.all, 'transcript', id] as const,
    batchesAll: () => [...queryKeys.meetingTranscripts.all, 'batches'] as const,
    batches: (page: number, source: string | undefined) =>
      [
        ...queryKeys.meetingTranscripts.batchesAll(),
        page,
        source ?? 'all',
      ] as const,
    eventsAll: () => [...queryKeys.meetingTranscripts.all, 'events'] as const,
    events: (query: unknown) =>
      [...queryKeys.meetingTranscripts.eventsAll(), query] as const,
  },
  backofficeCache: {
    all: ['backofficeCache'] as const,
    helpArticles: () =>
      [...queryKeys.backofficeCache.all, 'helpArticles'] as const,
  },
  q3TiersAdmin: {
    all: ['q3TiersAdmin'] as const,
    runs: () => [...queryKeys.q3TiersAdmin.all, 'runs'] as const,
    rules: () => [...queryKeys.q3TiersAdmin.all, 'rules'] as const,
    settings: () => [...queryKeys.q3TiersAdmin.all, 'settings'] as const,
    securities: (params?: unknown) =>
      [...queryKeys.q3TiersAdmin.all, 'securities', params] as const,
    securitiesSummary: () =>
      [...queryKeys.q3TiersAdmin.all, 'securities', 'summary'] as const,
    verifyTickers: (tickers: Array<string>) =>
      [
        ...queryKeys.q3TiersAdmin.all,
        'verify-tickers',
        [...tickers].sort().join(','),
      ] as const,
    reclassifyCandidates: (params?: unknown) =>
      [
        ...queryKeys.q3TiersAdmin.all,
        'securities',
        'reclassify-candidates',
        params,
      ] as const,
    schedule: () => [...queryKeys.q3TiersAdmin.all, 'schedule'] as const,
    positions: (params?: unknown) =>
      [...queryKeys.q3TiersAdmin.all, 'positions', params] as const,
    positionBook: (params?: unknown) =>
      [...queryKeys.q3TiersAdmin.all, 'position-book', params] as const,
    positionBookClients: (key: string | null) =>
      [...queryKeys.q3TiersAdmin.all, 'position-book', 'clients', key] as const,
  },
  q3TiersProxies: {
    all: ['q3TiersProxies'] as const,
    assignments: (params?: unknown) =>
      [...queryKeys.q3TiersProxies.all, 'assignments', params] as const,
    summary: () => [...queryKeys.q3TiersProxies.all, 'summary'] as const,
    categories: (includeInactive?: boolean) =>
      [...queryKeys.q3TiersProxies.all, 'categories', includeInactive] as const,
    recipients: () => [...queryKeys.q3TiersProxies.all, 'recipients'] as const,
    audit: (plaidSecurityId: string) =>
      [...queryKeys.q3TiersProxies.all, 'audit', plaidSecurityId] as const,
    tickerCheck: (ticker: string) =>
      [...queryKeys.q3TiersProxies.all, 'ticker-check', ticker] as const,
    tickerSearch: (query: string) =>
      [...queryKeys.q3TiersProxies.all, 'ticker-search', query] as const,
  },
  rothRadarAlertRuns: {
    all: ['rothRadarAlertRuns'] as const,
    list: (params?: unknown) =>
      [...queryKeys.rothRadarAlertRuns.all, 'list', params] as const,
    detail: (id: string) =>
      [...queryKeys.rothRadarAlertRuns.all, 'detail', id] as const,
    currentAlerts: () =>
      [...queryKeys.rothRadarAlertRuns.all, 'currentAlerts'] as const,
    schedule: () => [...queryKeys.rothRadarAlertRuns.all, 'schedule'] as const,
    recipients: () =>
      [...queryKeys.rothRadarAlertRuns.all, 'recipients'] as const,
  },
  rothRadarEngagement: {
    all: ['rothRadarEngagement'] as const,
    summary: (params?: unknown) =>
      [...queryKeys.rothRadarEngagement.all, 'summary', params] as const,
    notifications: (params?: unknown) =>
      [...queryKeys.rothRadarEngagement.all, 'notifications', params] as const,
  },
} as const
